// feature/estado-filter-pills.
// Activos / Contestados / Cerrados pill toggle, defaulting to "Activos."
// Per Fiorella's request — most users want their active queue first.
// Pure frontend filter; composes with every other filter in the page
// (tipo / estado / abogado / search / time / TipoFalloTabs / dispatcher
// pipeline). Backend SELECTs untouched.
//
// Three-bucket model (Sprint X5-fix, validated against Alejandra
// Barreto's report 2026-05-08):
//   * Activos      — active workflow (default). Anything not in the
//                    other two buckets.
//   * Contestados  — pendiente_archivar (lawyer marked contestada,
//                    awaiting Reparto's terminal archive action). Sprint
//                    56's original intent kept these in Activos "for
//                    awareness," but with 150 production cases stuck
//                    there (Reparto's archival workflow gap, separate
//                    followup), individual lawyers like Alejandra had
//                    24+ contestadas crowding their active queue. The
//                    third pill surfaces them as a distinct bucket.
//   * Cerrados     — terminal states only: cerrado, archivado,
//                    devuelto, devuelto_a_reparto, error_marcado,
//                    sancion_archivo, sancion_inaplicacion_archivo,
//                    fallo_decidido_no_impugnar.
//
// Notes:
//   * No localStorage persistence (per-page useState only) so the
//     default-Activos UX is consistent on every visit.
//   * "Todos" pill retired with Sprint X5-fix — the three-bucket model
//     covers the workflow, and dropping it kept the pill row uniform.
//     Restore if user feedback asks for it.
//   * isCaseClosed kept as a thin compat wrapper around getCaseBucket
//     for any future external consumer.

import React from "react";

const CLOSED_ESTADOS = new Set([
  "cerrado",
  "devuelto",
  "devuelto_a_reparto",
  "error_marcado",
  "sancion_archivo",
  "sancion_inaplicacion_archivo",
  "fallo_decidido_no_impugnar",
  "archivado",
]);

// Sprint X5-fix — three-bucket discriminator. estado is the canonical
// lifecycle marker; contestada_at is exposed by the my-priority endpoint
// for display/sort but is NOT the bucket discriminator (a case in
// fallo_decidido_no_impugnar can carry contestada_at from a prior
// state but should still classify as cerrados).
export function getCaseBucket(c) {
  if (!c) return "activos";
  if (CLOSED_ESTADOS.has(c.estado)) return "cerrados";
  if (c.estado === "pendiente_archivar") return "contestados";
  return "activos";
}

// Backward-compat helper. Prefer getCaseBucket for new code.
export function isCaseClosed(c) {
  return getCaseBucket(c) === "cerrados";
}

export function filterByActiveStatus(cases, mode) {
  if (!cases) return cases;
  // Legacy "todos" mode — kept for back-compat with any caller that
  // still passes it. Sprint X5-fix dropped the Todos pill from the UI
  // but the function still answers the question.
  if (mode === "todos") return cases;
  if (mode === "cerrados")    return cases.filter((c) => getCaseBucket(c) === "cerrados");
  if (mode === "contestados") return cases.filter((c) => getCaseBucket(c) === "contestados");
  // default → activos (also catches null / undefined / unknown modes).
  return cases.filter((c) => getCaseBucket(c) === "activos");
}

const OPTIONS = [
  { key: "activos",     label: "Activos" },
  { key: "contestados", label: "Contestados" },
  { key: "cerrados",    label: "Cerrados" },
];

export default function EstadoFilterPills({ cases, value, onChange }) {
  const list = cases || [];
  // Sprint X5-fix — count by bucket. Single pass over the list.
  const counts = { activos: 0, contestados: 0, cerrados: 0 };
  for (const c of list) counts[getCaseBucket(c)]++;

  return (
    <div role="tablist" style={{
      display: "flex", gap: 6, flexWrap: "wrap",
      margin: "8px 0 12px",
    }}>
      {OPTIONS.map((opt) => {
        const active = value === opt.key;
        const count = counts[opt.key] || 0;
        return (
          <button key={opt.key} role="tab" type="button"
                  aria-selected={active}
                  onClick={() => onChange(opt.key)}
                  className={active ? "" : "secondary"}
                  style={{
                    padding: "4px 12px", fontSize: 12,
                    display: "inline-flex", alignItems: "center", gap: 6,
                  }}>
            <span>{opt.label}</span>
            <span style={{
              background: active ? "rgba(255,255,255,0.25)" : "var(--bg-subtle)",
              color: active ? "var(--on-accent)" : "var(--text-muted)",
              fontSize: 10, fontWeight: 600,
              padding: "1px 6px", borderRadius: 10,
              minWidth: 16, textAlign: "center",
            }}>{count}</span>
          </button>
        );
      })}
    </div>
  );
}
